import React, {FormEvent, Fragment} from 'react';
import {connect, MapDispatchToProps, MapStateToProps} from "react-redux";
import {Button, Card, CardContent, InputAdornment, TextField} from "@material-ui/core";
import InputMask from "react-input-mask";
import {addCreditCard, resetCreditCard} from "../../../vendors/actions/creditCard";
import {updatePayment} from '../../../vendors/actions/user'
import {CreditCardSerializer} from '../../../vendors/utils/serializers'
import {PencilIcon} from "../../../elements/svgIcons";
import {togglePaymentEdit} from "../../../vendors/actions/layout";


interface OwnProps {
    addCreditCard?: any,
    resetCreditCard?: any
    updatePayment?: any,

    [key: string]: any
}

interface ConnectStateProps {
    CreditCardForm: any
    User: any
}

interface ConnectDispatchProps {
    addCreditCard?: (data: any) => void;
    resetCreditCard?: (data: any) => void;
    updatePayment?: (data: any) => void;
    togglePaymentEdit: () => void;
}

type HeaderProps = OwnProps & ConnectStateProps & ConnectDispatchProps;


const CardNumberMask = (props: any) => {
    const {...other} = props;
    return (
        <InputMask
            {...other}
            placeholder={'0000 0000 0000 0000'}
            mask="9999 9999 9999 9999"
            maskChar=""/>
    )
};

const ExpireDateMask = (props: any) => {
    const {...other} = props;
    return (
        <InputMask
            {...other}
            placeholder={'MM/YY'}
            mask="99/99"
            maskChar=""/>
    )
};

const CvcMask = (props: any) => {
    const {...other} = props;
    return (
        <InputMask
            {...other}
            placeholder={''}
            mask="9999"
            maskChar=""/>
    )
};

const PostalCodeMask = (props: any) => {
    const {...other} = props;
    return (
        <InputMask
            {...other}
            placeholder={''}
            mask="99999"
            maskChar=""/>
    )
};


class EditCreditCard extends React.Component<HeaderProps> {
    componentDidMount() {
        this.setupCard();
    }

    setupCard() {
        const {User} = this.props;
        const card = User?.paymentMethods?.card;
        const postalCode = User?.paymentMethods?.billing_details?.address?.postal_code;
        if (card) {
            this.proceed(card.exp_month, 'exp_month');
            this.proceed(card.exp_year, 'exp_year');
            this.proceed(postalCode, 'postal_code');
        }
    }

    componentWillUnmount() {
        this.props.Layout.editPayment && this.props.togglePaymentEdit();
        this.props.resetCreditCard()
    }

    private proceed(value: string | null | number, field: string, validate: boolean = false) {
        this.props.addCreditCard({value, field, validate});
    }

    addCardNumber = (e: any, validate: boolean = false) => {
        const value = e.target.value;
        this.proceed(value, 'number', validate);
    };

    addExpireDate = (e: any, validate: boolean = false) => {
        const value = e.target.value;
        let exp_month = null;
        let exp_year = null;
        if (value) {
            const date = value.split('/');
            if (date[0] && date[0].length === 2) {
                exp_month = parseInt(date[0])
            }
            if (date[1] && date[1].length === 2) {
                exp_year = parseInt(date[1])
            }
        }
        this.proceed(exp_month, 'exp_month', validate);
        this.proceed(exp_year, 'exp_year', validate);
    };

    addCvC = (e: any, validate: boolean = false) => {
        const value = e.target.value;
        this.proceed(value, 'cvc', validate);
    };

    addPostalCode = (e: any, validate: boolean = false) => {
        const value = e.target.value;
        this.proceed(value, 'postal_code', validate);
    };

    toggleEditCard = () => {
        this.setupCard();
        this.props.togglePaymentEdit()
    };

    reject() {
        this.props.togglePaymentEdit();
        this.props.resetCreditCard()
    }

    submit() {
        const {CreditCardForm} = this.props;
        if (!CreditCardForm.valid) {
            for (const key in CreditCardForm.data) {
                this.proceed(CreditCardForm.data[key].value, key, true)
            }
            return false
        }
        const creditCard = CreditCardSerializer(CreditCardForm.data);
        this.props.updatePayment(creditCard);
    }

    render() {
        const {editPayment} = this.props.Layout;
        const {User} = this.props;
        const form = this.props.CreditCardForm.data;
        const cartType = (form.number.cardType || 'unknown').toLowerCase().replace(" ", "");
        let expireDate = '';
        const typeOfCard = User?.paymentMethods?.card?.brand;
        const last4 = User?.paymentMethods?.card?.last4;
        if (form?.exp_month?.value && form?.exp_year?.value) {
            expireDate = `${String(form?.exp_month?.value).padStart(2, '0')}/${form?.exp_year?.value.toString().slice(-2)}`;
        }
        let cardInfo: any = '';
        if (User.paymentMethods) {
            cardInfo =
                <div className="card-number d-flex">
                    <div className={`card-icon-holder card-icon-${typeOfCard} mr-2`}/>
                    **** **** **** {last4}
                </div>
        } else {
            cardInfo =
                <div className='card-number no-card d-flex'>
                    <div className={`card-icon-holder card-icon-unknown mr-3`}/>
                    Please add payment details
                </div>
        }

        return (
            <Card className="card-box px-3 px-sm-4 pt-3 pb-2 pb-sm-0">
                <div className="payment-info rounded border-0">
                    <span className='heading'>Payment Details</span>
                    {!editPayment && <PencilIcon className='payment-edit' onClick={this.toggleEditCard}/>}
                    {!editPayment && cardInfo}
                </div>
                <CardContent className="p-2 pt-4 px-sm-0">
                    {editPayment &&
                    <Fragment>
                        <div className={'text-field-custom-mb card-number'}>
                            <TextField
                                fullWidth
                                variant="outlined"
                                label="Card Number"
                                error={!!form?.number?.error && form.number.dirty}
                                helperText={form.number.dirty && form?.number?.error}
                                onBlur={(e: FormEvent) => {
                                    this.addCardNumber(e, true)
                                }}
                                onChange={this.addCardNumber}
                                id="card-number"
                                InputProps={{
                                    endAdornment: <InputAdornment position="end"><span
                                        className={`card-icon-holder card-icon-${cartType}`}/></InputAdornment>,
                                    inputComponent: CardNumberMask,
                                }}
                            />
                        </div>
                        <div className={'text-field-custom-mb card-number'}>
                            <TextField
                                fullWidth
                                variant="outlined"
                                label="Expiration Date"
                                error={(!!form?.exp_month?.error || !!form?.exp_year?.error) && (form.exp_month.dirty || form.exp_year.dirty)}
                                helperText={(form.exp_month.dirty || form.exp_year.dirty) && (form?.exp_month?.error || form?.exp_year?.error)}
                                defaultValue={expireDate}
                                onBlur={(e: FormEvent) => {
                                    this.addExpireDate(e, true)
                                }}
                                onChange={this.addExpireDate}
                                id="expire-date"
                                InputProps={{
                                    inputComponent: ExpireDateMask,
                                }}
                            >
                            </TextField>
                        </div>
                        <div className={'text-field-custom-mb card-number'}>
                            <TextField
                                fullWidth
                                error={!!form?.cvc?.error && form.cvc.dirty}
                                helperText={form.cvc.dirty && form?.cvc?.error}
                                onBlur={(e: FormEvent) => this.addCvC(e, true)}
                                onChange={this.addCvC}
                                label="CVC"
                                id="cvc"
                                variant="outlined"
                                InputProps={{
                                    inputComponent: CvcMask,
                                }}
                            />
                        </div>
                        <div className={'text-field-custom-mb card-number'}>
                            <TextField
                                fullWidth
                                variant="outlined"
                                label="Zip Code"
                                error={!!form?.postal_code?.error && form.postal_code.dirty}
                                helperText={form.postal_code.dirty && form?.postal_code?.error}
                                defaultValue={form?.postal_code?.value}
                                onBlur={(e: FormEvent) => this.addPostalCode(e, true)}
                                onChange={this.addPostalCode}
                                id="postal-code"
                                InputProps={{
                                    inputComponent: PostalCodeMask,
                                }}
                            />
                        </div>
                        <div className='text-center text-sm-right my-4'>
                            <Button
                                onClick={(e) => this.submit()}
                                size={'small'}
                                color="secondary"
                                variant="contained"
                                className="text-white mr-3">
                                Save Changes
                            </Button>
                            <Button
                                onMouseDown={(e) => this.reject()}
                                size={'small'}
                                color="secondary"
                                variant="outlined"
                                className="text-purple">
                                Never Mind
                            </Button>
                        </div>
                    </Fragment>}
                </CardContent>
            </Card>
        )
    }
}

type ConnectStateMapper = MapStateToProps<ConnectStateProps, OwnProps>

const mapStateToProps: ConnectStateMapper = (state: any) => {
    const {CreditCardForm, User, Layout} = state;
    return {
        CreditCardForm,
        User,
        Layout
    }
};

type ConnectDispatchMapper = MapDispatchToProps<ConnectDispatchProps, OwnProps>

const mapDispatchToProps: ConnectDispatchMapper = (dispatch: any) => {
    return {
        addCreditCard: (data: any) => dispatch(addCreditCard(data)),
        resetCreditCard: () => dispatch(resetCreditCard()),
        updatePayment: (data: any) => dispatch(updatePayment(data)),
        togglePaymentEdit: () => dispatch(togglePaymentEdit())
    }
};


export default connect(mapStateToProps, mapDispatchToProps)(EditCreditCard);
